import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { motion, AnimatePresence } from "framer-motion";
import { format, differenceInCalendarDays, addDays, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, ShieldCheck, Star, Loader2, Lock,
  MessageCircle, Wallet, Home, Pencil, ChevronDown, Users, Sparkles, LogIn,
} from "lucide-react";
import { StayHeader } from "@/components/stays/StayHeader";
import { AuthModal } from "@/components/auth/AuthModal";
import { ContactRecap } from "@/components/stays/booking/ContactRecap";
import { GuestSteppers } from "@/components/stays/booking/GuestSteppers";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { getProperty, getRooms, getReviews, getAvailability, createBooking, bookingError, fmtAmount, getRules, getFeeQuote, istToday, waitForPaymentLink, validPhoneIntl, validEmailAddr } from "@/lib/stayflowApi";
import { PriceBreakdown } from "@/components/stays/booking/PriceBreakdown";
import { guestLimits, fmtTime } from "@/lib/propertyRules";
import { RulesAgreement } from "@/components/stays/booking/RulesAgreement";
import { getProfile, saveProfile } from "@/lib/profile";
import { GstInvoiceFields } from "@/components/stays/booking/GstInvoiceFields";
import { emptyGst, gstClientError, gstPayload, gstBackendError, billingConflict } from "@/lib/gstBilling";
import { apiLinkBooking } from "@/lib/userAuth";

const iso = (d) => format(d, "yyyy-MM-dd");
const validName = (v) => !!(v || "").trim();

const PayBadge = ({ children }) => (
  <span className="inline-flex items-center rounded-lg border border-black/10 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink-soft">{children}</span>
);

const StepShell = ({ n, title, status, onEdit, children, summary }) => {
  const complete = status === "complete";
  const active = status === "active";
  return (
    <div className={`rounded-3xl border bg-white transition-colors ${active ? "border-black/15 shadow-[0_10px_40px_rgba(20,20,20,0.06)]" : "border-black/8"}`} data-testid={`checkout-step-${n}`}>
      <div className="flex items-center gap-3 px-6 py-5">
        <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${complete ? "bg-emerald-500 text-white" : active ? "bg-ink text-white" : "bg-paper-muted text-ink-faint"}`}>
          {complete ? <CheckCircle2 className="w-5 h-5" /> : n}
        </span>
        <h2 className={`font-heading font-bold text-lg tracking-tight ${active || complete ? "text-ink" : "text-ink-faint"}`}>{title}</h2>
        {complete && onEdit && (
          <button onClick={onEdit} data-testid={`checkout-edit-${n}`} className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-brand transition-colors">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>
      {complete && summary && <div className="px-6 pb-5 -mt-2 pl-[68px]">{summary}</div>}
      <AnimatePresence initial={false}>
        {active && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: "easeOut" }} className="overflow-hidden">
            <div className="px-6 pb-6 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GuestFields = ({ form, onForm, gst, onGst, gstError }) => (
  <>
    <div>
      <Label htmlFor="cc_name" className="text-xs text-ink-soft">Full name</Label>
      <Input id="cc_name" name="guest_name" value={form.guest_name} onChange={onForm} placeholder="Your name" data-testid="checkout-name" className="mt-1 h-11 focus-visible:ring-brand" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label htmlFor="cc_phone" className="text-xs text-ink-soft">WhatsApp number (with country code)</Label>
        <Input id="cc_phone" name="guest_phone" type="tel" value={form.guest_phone} onChange={onForm} placeholder="+91 98765 43210" data-testid="checkout-phone" className="mt-1 h-11 focus-visible:ring-brand" />
      </div>
      <div>
        <Label htmlFor="cc_email" className="text-xs text-ink-soft">Email</Label>
        <Input id="cc_email" name="guest_email" type="email" value={form.guest_email} onChange={onForm} placeholder="you@email.com" data-testid="checkout-email" className="mt-1 h-11 focus-visible:ring-brand" />
      </div>
    </div>
    <p className="text-[11px] text-ink-faint" data-testid="checkout-whatsapp-note">We'll send your booking updates here.</p>
    <p className="text-[11px] text-ink-faint">Indian or foreign number — always include the country code, e.g. +44 7400 123456.</p>
    <p className="text-[11px] text-ink-faint" data-testid="checkout-privacy-note">
      We use your phone and email only for this booking. <Link to="/privacy" className="underline hover:text-ink">Privacy policy</Link>
    </p>
    <GstInvoiceFields value={gst} onChange={onGst} error={gstError} idPrefix="cc-" />
  </>
);

const CheckoutConfirm = () => {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const propertyId = sp.get("propertyId");
  const roomId = sp.get("roomId");

  // Desktop-only page. On smaller screens hand off to the existing mobile flow.
  const [isDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    if (!isDesktop && propertyId && roomId) {
      const q = new URLSearchParams(sp); q.delete("propertyId"); q.delete("roomId");
      navigate(`/stays/${propertyId}/${roomId}${q.toString() ? `?${q}` : ""}`, { replace: true });
    }
  }, [isDesktop, propertyId, roomId, navigate, sp]);

  const propQ = useQuery({ queryKey: ["property", propertyId], queryFn: () => getProperty(propertyId), enabled: !!propertyId, staleTime: 5 * 60 * 1000 });
  const roomsQ = useQuery({ queryKey: ["rooms", propertyId], queryFn: () => getRooms(propertyId), enabled: !!propertyId, staleTime: 5 * 60 * 1000 });
  const reviewsQ = useQuery({ queryKey: ["reviews", propertyId], queryFn: () => getReviews(propertyId), enabled: !!propertyId, staleTime: 30 * 60 * 1000 });
  const property = propQ.data;
  const rev = reviewsQ.data;
  const room = useMemo(() => (roomsQ.data || []).find((r) => String(r.id) === String(roomId)), [roomsQ.data, roomId]);

  const today = useMemo(() => istToday(), []);
  const windowEnd = useMemo(() => addDays(today, 180), [today]);
  const availQ = useQuery({
    queryKey: ["availability", roomId],
    queryFn: () => getAvailability(roomId, iso(today), iso(windowEnd)),
    enabled: !!roomId,
    staleTime: 0, gcTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true,
  });
  const busyDates = useMemo(() => (availQ.data?.busy_days || []).map((s) => { const d = parseISO(s); d.setHours(0, 0, 0, 0); return d; }), [availQ.data]);

  const rulesQ = useQuery({ queryKey: ["rules", propertyId], queryFn: () => getRules(propertyId), enabled: !!propertyId, staleTime: 5 * 60 * 1000 });
  const rules = rulesQ.data?.recorded === false ? null : rulesQ.data || null;

  const [range, setRange] = useState(() => {
    const ci = sp.get("checkIn"), co = sp.get("checkOut");
    try {
      if (ci && co) {
        const from = parseISO(ci), to = parseISO(co);
        if (from >= istToday() && to > from) return { from, to };
      }
    } catch { /* ignore */ }
    return undefined;
  });
  const [adults, setAdults] = useState(() => Math.max(1, Number(sp.get("adults")) || Number(sp.get("guests")) || 2));
  const [children, setChildren] = useState(() => Math.max(0, Number(sp.get("children")) || 0));
  const { maxAdults, maxChildren, childRuleText } = useMemo(() => guestLimits(room), [room]);
  const childrenAllowed = rules?.children_allowed?.value !== false && maxChildren > 0;
  useEffect(() => {
    if (!room) return; // limits are unknown until the room loads — don't clamp yet
    if (!childrenAllowed && children > 0) setChildren(0);
    if (adults > maxAdults) setAdults(maxAdults);
    if (children > maxChildren) setChildren(maxChildren);
  }, [room, maxAdults, adults, children, maxChildren, childrenAllowed]);

  const [form, setForm] = useState(() => ({ guest_name: "", guest_phone: "", guest_email: "", special_requests: "", ...getProfile() }));
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, guest_name: f.guest_name || user.name || "", guest_email: f.guest_email || user.email || "", guest_phone: f.guest_phone || user.phone || "" }));
  }, [user]);
  const onForm = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const [gst, setGst] = useState(emptyGst);
  const [gstError, setGstError] = useState("");
  const onGst = (g) => { setGst(g); setGstError(""); };

  const contactComplete = validName(form.guest_name) && validPhoneIntl(form.guest_phone) && validEmailAddr(form.guest_email);
  // Based on the signed-in account's own fields (stable — unaffected by typing in the form).
  const userComplete = !!user && validName(user.name) && validPhoneIntl(user.phone) && validEmailAddr(user.email);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;

  const quoteQ = useQuery({
    queryKey: ["feeQuote", roomId, range?.from ? iso(range.from) : null, range?.to ? iso(range.to) : null, adults, children],
    queryFn: () => getFeeQuote({ room_id: roomId, check_in: iso(range.from), check_out: iso(range.to), adults, children }),
    enabled: !!roomId && nights > 0,
    staleTime: 60 * 1000,
  });
  const quote = quoteQ.data || null;
  const quoteError = quoteQ.isError ? bookingError(quoteQ.error) : "";
  const quoteTerms = quote?.cancellation_terms || null;
  const [agreed, setAgreed] = useState(false);

  const [step, setStep] = useState(1);
  const [authOpen, setAuthOpen] = useState(false);

  // Shared by both the guest and signed-in versions of step 1.
  const continueFromStep1 = () => {
    if (!range?.from || !range?.to || nights < 1) { toast.error("Please select your dates first."); setEditing("dates"); return; }
    if (!validName(form.guest_name)) { toast.error("Please enter your full name."); return; }
    if (!validPhoneIntl(form.guest_phone)) { toast.error("Enter a WhatsApp number with its country code, e.g. +91 98765 43210."); return; }
    if (!validEmailAddr(form.guest_email)) { toast.error("Please enter a valid email address."); return; }
    const gErr = gstClientError(gst);
    if (gErr) { setGstError(gErr); toast.error(gErr); return; }
    setStep(2);
  };

  const [editing, setEditing] = useState(() => (sp.get("checkIn") && sp.get("checkOut")) ? "" : "dates");
  const autoRef = useRef(false);

  // Evaluate the auto-skip ONCE after auth resolves. Returning guests whose account
  // already has name+phone+email jump straight to Step 2; everyone else stays on Step 1
  // (so typing in the contact form never yanks the step out from under them).
  useEffect(() => {
    if (authLoading || autoRef.current || !user) return;
    autoRef.current = true;
    if (userComplete) setStep(2);
  }, [authLoading, user, userComplete]);

  // No booking is created while browsing — only when the guest taps Pay (same as mobile).
  const bookingBody = () => ({
    room_id: roomId,
    guest_name: form.guest_name.trim(),
    guest_phone: form.guest_phone.trim().replace(/\s/g, ""),
    guest_email: form.guest_email.trim(),
    check_in: iso(range.from),
    check_out: iso(range.to),
    guests: adults + children, adults, children, rooms: 1,
    source: "website",
    special_requests: (form.special_requests || "").trim(),
    ...gstPayload(gst),
  });

  // The amount Razorpay will charge, exactly as the server quotes it.
  const totalAmount = quote?.amount_payable ?? null;

  const [confirming, setConfirming] = useState(false);
  const [awaitingLink, setAwaitingLink] = useState(false);
  const [payError, setPayError] = useState("");
  // One attempt per tap. Failures show a message with a manual "Try again" — never auto-retried.
  const handleConfirm = async () => {
    if (confirming) return;
    if (!range?.from || !range?.to || nights < 1) { toast.error("Please select your dates."); return; }
    if (!contactComplete) { toast.error("Please complete your contact details."); setStep(1); return; }
    const gErr = gstClientError(gst);
    if (gErr) { setGstError(gErr); setStep(1); return; }
    setConfirming(true);
    setPayError("");
    let b;
    try {
      b = await createBooking(bookingBody());
      setGstError("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 && gst.enabled) { setGstError(gstBackendError(err)); setStep(1); }
      else if (status === 409 && billingConflict(err)) setPayError("Billing details can't be changed after payment.");
      else setPayError(bookingError(err));
      setConfirming(false);
      return;
    }
    try {
      saveProfile({ guest_name: form.guest_name.trim(), guest_email: form.guest_email.trim(), guest_phone: form.guest_phone.trim().replace(/\s/g, "") });
      const bill = gstPayload(gst);
      localStorage.setItem("sf_last_booking", JSON.stringify({ id: b.id, phone: form.guest_phone.trim().replace(/\s/g, ""), email: form.guest_email.trim(), property_id: propertyId, check_in: b.check_in, check_out: b.check_out, bill_to_company: bill.bill_to_company, bill_to_gstin: bill.bill_to_gstin, payment_message: b.payment_message || "" }));
    } catch { /* storage unavailable */ }
    if (user) {
      try {
        await apiLinkBooking({ booking_id: b.id, property_id: propertyId, property_name: property?.name, room_name: room?.name, check_in: b.check_in, check_out: b.check_out, amount: b.amount_payable ?? totalAmount, status: b.status || "pending", cover_image: room?.cover_image });
      } catch (e) { console.warn("link booking failed", e); }
    }
    // The link may arrive a few seconds after the booking — re-check payment-status, never re-book.
    let link = b.payment_link_url;
    if (!link) { setAwaitingLink(true); link = await waitForPaymentLink(b.id); setAwaitingLink(false); }
    if (link) { window.location.href = link; return; }
    navigate(`/booking/${b.id}/status`);
  };

  const cover = room?.cover_image || (room?.photos || [])[0]?.url;
  const guestsLabel = `${adults} Adult${adults !== 1 ? "s" : ""}${children > 0 ? `, ${children} Child${children !== 1 ? "ren" : ""}` : ""}`;
  const datesLabel = range?.from && range?.to ? `${format(range.from, "dd")} – ${format(range.to, "dd MMM yyyy")} · ${nights} Night${nights !== 1 ? "s" : ""}` : "Select dates";

  if (!isDesktop) return null;

  const notReady = propQ.isLoading || roomsQ.isLoading;
  if (!notReady && !room) {
    return (
      <div className="bg-paper min-h-screen">
        <StayHeader />
        <div className="max-w-6xl mx-auto px-8 py-20 text-center" data-testid="checkout-missing">
          <p className="text-ink font-medium">We couldn't load that reservation.</p>
          <button onClick={() => navigate(`/stays/${propertyId || ""}`)} className="mt-4 rounded-xl bg-ink text-white px-5 py-2.5 text-sm font-semibold">Back to stays</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-screen hidden lg:block" data-testid="checkout-confirm-page">
      <StayHeader />
      <div className="max-w-6xl mx-auto px-8 py-10">
        {/* Top bar */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/stays/${propertyId}`)} data-testid="checkout-back" className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center text-ink hover:bg-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-extrabold text-ink tracking-tight font-heading">Confirm and pay</h1>
        </div>

        <div className="grid grid-cols-12 gap-12 items-start mt-6">
          {/* LEFT — stepper */}
          <div className="col-span-7 space-y-4">
            {/* STEP 1 */}
            <StepShell
              n={1} title="Identification & contact"
              status={step === 1 ? "active" : "complete"}
              onEdit={() => { autoRef.current = true; setStep(1); }}
              summary={(
                <div className="flex items-center gap-2 text-sm text-ink-soft" data-testid="checkout-step1-summary">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold text-ink">{form.guest_name || user?.name || "Guest"}</span>
                  <span>· {[form.guest_phone, form.guest_email].filter(Boolean).join(" · ")}</span>
                </div>
              )}
            >
              {!user ? (
                <div className="space-y-4" data-testid="checkout-contact-form">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-paper-muted/70 border border-black/8 px-3.5 py-2.5">
                    <p className="text-sm text-ink-soft flex-1">Booking as a guest — no account needed. We'll send your confirmation, host contact and directions to the details below.</p>
                    <button type="button" onClick={() => setAuthOpen(true)} data-testid="checkout-signin-optional" className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border border-black/15 bg-white px-4 h-9 text-sm font-semibold text-ink hover:bg-paper transition-colors">
                      <LogIn className="w-4 h-4" /> Sign in (optional)
                    </button>
                  </div>
                  <GuestFields form={form} onForm={onForm} gst={gst} onGst={onGst} gstError={gstError} />
                  <button onClick={continueFromStep1} data-testid="checkout-step1-continue" className="w-full rounded-xl bg-ink hover:bg-black text-white font-semibold h-12 transition-colors">
                    Continue
                  </button>
                </div>
              ) : (
                <div className="space-y-4" data-testid="checkout-contact-form">
                  <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3.5 py-2.5 text-sm text-emerald-800">
                    <ShieldCheck className="w-4 h-4 shrink-0" /> Signed in{user.name ? ` as ${user.name}` : ""}. Confirm your contact details below.
                  </div>
                  <GuestFields form={form} onForm={onForm} gst={gst} onGst={onGst} gstError={gstError} />
                  <button onClick={continueFromStep1} data-testid="checkout-step1-continue" className="w-full rounded-xl bg-ink hover:bg-black text-white font-semibold h-12 transition-colors">
                    Continue
                  </button>
                </div>
              )}
            </StepShell>

            {/* STEP 2 */}
            <StepShell
              n={2} title="Payment"
              status={step === 2 ? "active" : step > 2 ? "complete" : "upcoming"}
              onEdit={() => setStep(2)}
              summary={nights > 0 && totalAmount != null ? (
                <div className="text-sm text-ink-soft" data-testid="checkout-step2-summary">
                  Pay <span className="font-semibold text-ink">{fmtAmount(totalAmount)}</span> securely via Razorpay
                </div>
              ) : null}
            >
              {nights > 0 ? (
                <div className="space-y-4" data-testid="checkout-deposit">
                  <div className="rounded-2xl border-2 border-brand/40 bg-brand-soft/30 p-5">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-dark"><Sparkles className="w-3.5 h-3.5" /> PAY TO CONFIRM</span>
                      <Wallet className="w-5 h-5 text-brand" />
                    </div>
                    <p className="mt-2 font-heading font-extrabold text-3xl text-ink" data-testid="checkout-pay-amount">{totalAmount != null ? fmtAmount(totalAmount) : "Checking price…"}</p>
                    <div className="mt-3"><PriceBreakdown quote={quote} nights={nights} loading={quoteQ.isFetching} error={quoteError} idPrefix="checkout-pay-" /></div>
                    <p className="text-xs text-ink-soft mt-2">Nothing is charged or held until you tap Pay.</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-ink-faint mb-2">Secure payment options</p>
                    <div className="flex flex-wrap gap-2">
                      <PayBadge>UPI · GPay · PhonePe · Paytm</PayBadge>
                      <PayBadge>Visa · Mastercard · RuPay</PayBadge>
                      <PayBadge>NetBanking</PayBadge>
                    </div>
                  </div>
                  <button onClick={() => setStep(3)} data-testid="checkout-continue-review" className="w-full rounded-xl bg-ink hover:bg-black text-white font-semibold h-12 transition-colors">
                    Continue to review
                  </button>
                </div>
              ) : (
                <p className="text-sm text-ink-soft py-6">Select your dates to see the price.</p>
              )}
            </StepShell>

            {/* STEP 3 */}
            <StepShell n={3} title="Review & property rules" status={step === 3 ? "active" : "upcoming"}>
              <div className="space-y-4" data-testid="checkout-review">
                {(rules?.check_in_from || rules?.check_out_by) && (
                  <div className="flex items-start gap-3 rounded-2xl bg-paper-muted/60 border border-black/5 px-4 py-3" data-testid="checkout-stay-times">
                    <Home className="w-4 h-4 text-ink mt-0.5 shrink-0" />
                    <p className="text-sm text-ink-soft">
                      {rules.check_in_from && <>Check-in from <span className="font-semibold text-ink">{fmtTime(rules.check_in_from)}</span>{rules.check_in_until ? ` until ${fmtTime(rules.check_in_until)}` : ""}</>}
                      {rules.check_in_from && rules.check_out_by ? " · " : ""}
                      {rules.check_out_by && <>Check-out by <span className="font-semibold text-ink">{fmtTime(rules.check_out_by)}</span></>}
                    </p>
                  </div>
                )}
                <RulesAgreement
                  quoteTerms={quoteTerms} rules={rules} loading={quoteQ.isFetching}
                  agreed={agreed} setAgreed={setAgreed} idPrefix="cc-"
                />
                <ContactRecap phone={form.guest_phone} email={form.guest_email} testid="checkout-contact-recap" />
                <div className="flex items-start gap-3 rounded-2xl bg-paper-muted/60 border border-black/5 px-4 py-3">
                  <MessageCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-soft"><span className="font-semibold text-ink">WhatsApp delivery:</span> your PDF invoice and the exact Google Maps location pin are sent to {form.guest_phone || "your mobile number"}.</p>
                </div>
                {payError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50/70 px-4 py-3 text-sm text-red-700" role="alert" data-testid="checkout-pay-error">{payError}</div>
                )}
                <button onClick={handleConfirm} disabled={confirming || !agreed || totalAmount == null} data-testid="checkout-pay-confirm"
                  className="w-full rounded-2xl bg-brand hover:bg-brand-dark text-white font-heading font-extrabold py-4 text-lg shadow-xl shadow-brand/25 active:scale-[0.99] transition flex items-center justify-center gap-2 disabled:opacity-60">
                  {confirming ? <><Loader2 className="w-5 h-5 animate-spin" /> {awaitingLink ? "Setting up secure payment…" : "Redirecting to secure payment…"}</> : payError ? <><Lock className="w-5 h-5" /> Try again</> : <><Lock className="w-5 h-5" /> Pay {fmtAmount(totalAmount)} & confirm reservation</>}
                </button>
                <p className="text-center text-xs text-ink-faint">By continuing you agree to StayFlow's Terms of Service and the property's rules.</p>
              </div>
            </StepShell>
          </div>

          {/* RIGHT — sticky trip summary */}
          <aside className="col-span-5">
            <div className="sticky top-24 bg-white border border-black/8 rounded-3xl p-6 shadow-xl space-y-5" data-testid="checkout-summary">
              <div className="flex gap-4">
                {cover && <img src={cover} alt={room?.name} className="w-24 h-24 rounded-2xl object-cover shrink-0" />}
                <div className="min-w-0">
                  <p className="font-heading font-bold text-ink leading-snug line-clamp-2">{property?.name}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-ink-soft">
                    {rev?.rating > 0 && <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" /> {Number(rev.rating).toFixed(2)} ({rev.total})</span>}
                    <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Partner</span>
                  </div>
                  <p className="mt-1 text-sm text-ink truncate">{room?.name}</p>
                </div>
              </div>

              <div className="border-t border-black/5 pt-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">Dates</p>
                    <p className="text-sm font-medium text-ink mt-0.5" data-testid="checkout-summary-dates">{datesLabel}</p>
                  </div>
                  <button onClick={() => setEditing(editing === "dates" ? "" : "dates")} data-testid="checkout-change-dates" className="text-sm font-semibold text-brand hover:underline shrink-0">Change</button>
                </div>
                {editing === "dates" && (
                  <div className="rounded-2xl border border-black/10 p-2 flex justify-center">
                    <div className="rdp-wrap" style={{ "--rdp-cell-size": "34px" }}>
                      <DayPicker mode="range" selected={range} onSelect={(r) => { setRange(r); if (r?.from && r?.to && r.from.getTime() !== r.to.getTime()) setTimeout(() => setEditing(""), 250); }}
                        defaultMonth={range?.from || today} numberOfMonths={1} fromDate={today} toDate={windowEnd} disabled={[{ before: today }, ...busyDates]} data-testid="checkout-datepicker" />
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">Guests</p>
                    <p className="text-sm font-medium text-ink mt-0.5 inline-flex items-center gap-1.5" data-testid="checkout-summary-guests"><Users className="w-3.5 h-3.5 text-ink-faint" /> {guestsLabel}</p>
                  </div>
                  <button onClick={() => setEditing(editing === "guests" ? "" : "guests")} data-testid="checkout-change-guests" className="text-sm font-semibold text-brand hover:underline shrink-0">Change</button>
                </div>
                {editing === "guests" && (
                  <div className="rounded-2xl border border-black/10 px-4">
                    <GuestSteppers adults={adults} setAdults={setAdults} childrenCount={children} setChildrenCount={setChildren} maxAdults={maxAdults} maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed} idPrefix="cc-" />
                  </div>
                )}
              </div>

              <div className="border-t border-black/5 pt-4" data-testid="checkout-summary-bill">
                {nights > 0
                  ? <PriceBreakdown quote={quote} nights={nights} loading={quoteQ.isFetching} error={quoteError} idPrefix="checkout-summary-" />
                  : <p className="text-sm text-ink-soft">Select your dates to see the price.</p>}
              </div>

              <div className="rounded-2xl bg-paper-muted/70 border border-black/5 p-4" data-testid="checkout-deposit-split">
                <p className="text-xs text-ink-soft">You won't be charged, and no room is held, until you tap Pay.</p>
              </div>

              <div className="flex flex-col gap-1.5 pt-1 text-[11px] text-ink-soft">
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-brand" /> 256-bit SSL encrypted payment · Razorpay.</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        title="Sign in (optional)"
        description="Signing in just autofills your details and keeps this booking in your account."
      />
    </div>
  );
};

export default CheckoutConfirm;
